// The clinic app's impersonation controller (Frontend §6.4): one per app, built in `createClinicApp`. It owns the
// stored state, the header scope the fetcher asks for, and where to go when the session ends.
import {
  adminClinicHref,
  clearImpersonation,
  impersonationIdFor,
  readImpersonation,
  writeImpersonation,
  type ImpersonationState,
  type KeyValueStore,
} from './state'

export interface ImpersonationController {
  /** The stored session, or null. */
  current(): ImpersonationState | null
  /** The `#imp=` landing: a new session replaces any old one. */
  start(id: string): void
  /** `/v1/me` confirmed the session for this clinic: its routes get the header from now on. */
  confirm(clinicId: string): void
  /** `getImpersonationId(path)` for `configureApi` (Frontend §3.2). */
  headerFor(path: string): string | null
  clear(): void
  /** S-04 for the impersonated clinic with the "Impersonation ended" notice, or null when the clinic isn't known. */
  adminReturnHref(): string | null
  /** Leaves the clinic app for the admin site (`window.location.assign`; tests and stories pass a spy). */
  assign(href: string): void
}

export function createImpersonation(options: {
  adminOrigin: string
  assign?: (href: string) => void
  storage?: KeyValueStore | null
}): ImpersonationController {
  const storage = options.storage
  const read = () => (storage === undefined ? readImpersonation() : readImpersonation(storage))
  const write = (s: ImpersonationState) => {
    if (storage === undefined) writeImpersonation(s)
    else writeImpersonation(s, storage)
  }
  // The stored value is read once and kept in memory, so the header never depends on storage being available.
  let state: ImpersonationState | null = read()
  return {
    current: () => state,
    start(id) {
      state = { id }
      write(state)
    },
    confirm(clinicId) {
      if (!state || state.clinicId === clinicId) return
      state = { ...state, clinicId }
      write(state)
    },
    headerFor: (path) => impersonationIdFor(path, state),
    clear() {
      state = null
      if (storage === undefined) clearImpersonation()
      else clearImpersonation(storage)
    },
    adminReturnHref: () => (state?.clinicId ? adminClinicHref(options.adminOrigin, state.clinicId) : null),
    assign: (href) => {
      if (options.assign) options.assign(href)
      else window.location.assign(href)
    },
  }
}
